import { Loader2Icon, Pause, Play, User } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useGetStudentTuitionDetails } from '~entities/student/api/get-student-tuition-details';
import { useUpdateClassChargeMutation } from '~entities/student/api/update-class-charge';

import { useGetCurrency } from '~shared/api/currency-api';
import { Button } from '~shared/shadcn/ui/button';
import { Card, CardContent } from '~shared/shadcn/ui/card';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';
import { ScrollArea } from '~shared/shadcn/ui/scroll-area';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~shared/shadcn/ui/tabs';

interface StopChargingStudentProps {
  studentId: string;
  studentName: string;
}

export const StopChargingStudent = ({
  studentId,
  studentName,
}: StopChargingStudentProps) => {
  const { data: currencyDetails } = useGetCurrency();

  const { data: activeTuition } = useGetStudentTuitionDetails({
    studentId,
    status: 'active',
  });

  const { data: pausedTuition } = useGetStudentTuitionDetails({
    studentId,
    status: 'paused',
  });

  const [pauseType, setPauseType] = useState('entire');
  const [selectedClasses, setSelectedClasses] = useState<Array<string>>([]);
  const [selectedTab, setSelectedTab] = useState<'resume' | 'pause'>('pause');

  const mutation = useUpdateClassChargeMutation();

  const hasActiveTuition = (activeTuition?.length ?? 0) > 0;
  const hasSelectedClasses = selectedClasses.length > 0;
  const isSubmitDisabled =
    mutation.isPending ||
    (selectedTab === 'pause' &&
      (pauseType === 'entire' ? !hasActiveTuition : !hasSelectedClasses)) ||
    (selectedTab === 'resume' && !hasSelectedClasses);

  const handleClassToggle = (classId: string) => {
    setSelectedClasses((prev) =>
      prev.includes(classId)
        ? prev.filter((id) => id !== classId)
        : [...prev, classId],
    );
  };

  const handleUpdateCharge = async () => {
    try {
      await mutation.mutateAsync({
        studentId,
        type: selectedTab,
        entityType: 'enrollment',
        entityId:
          selectedTab === 'pause' && pauseType === 'entire'
            ? (activeTuition?.map((c) => c.enrollmentId) ?? [])
            : selectedClasses,
      });
      setSelectedClasses([]);
      toast.success('Tuition details updated successfully');
    } catch (error) {
      console.error('Error updating tuition details:', error);
      toast.error('Failed to update tuition details');
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant='outline' className='w-full'>
          <Pause />
          Pause Charges Settings
        </Button>
      </DialogTrigger>
      <DialogContent className='max-h-[90vh] max-w-2xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <User className='size-5' />
            Manage Student Charges
          </DialogTitle>
          <DialogDescription>
            Pause or resume charges for {studentName} (Student ID: {studentId})
          </DialogDescription>
        </DialogHeader>

        <Tabs
          defaultValue='pause'
          className='w-full'
          onValueChange={(e) => {
            setSelectedClasses([]);
            setSelectedTab(e as 'pause' | 'resume');
          }}
        >
          <TabsList className='grid w-full grid-cols-2'>
            <TabsTrigger value='pause' className='flex items-center gap-2'>
              <Pause className='size-4' />
              Pause Charges
            </TabsTrigger>
            <TabsTrigger value='resume' className='flex items-center gap-2'>
              <Play className='size-4' />
              Resume Charges
            </TabsTrigger>
          </TabsList>

          <TabsContent value='pause' className='mt-6 space-y-6'>
            <div className=''>
              <div>
                <Label className='text-base font-medium'>Pause Type</Label>
                <RadioGroup
                  value={pauseType}
                  onValueChange={setPauseType}
                  className='mt-2'
                >
                  <div className='flex items-center space-x-2'>
                    <RadioGroupItem value='entire' id='entire' />
                    <Label htmlFor='entire'>
                      Pause charges for entire student
                    </Label>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <RadioGroupItem value='selected' id='selected' />
                    <Label htmlFor='selected'>
                      Pause charges for selected classes only
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {pauseType === 'entire' && !hasActiveTuition && (
                <Card className='mt-6'>
                  <CardContent className='flex flex-col items-center justify-center py-8 text-center'>
                    <h3 className='mb-2 text-lg font-medium text-gray-900'>
                      No Active Charges
                    </h3>
                    <p className='text-gray-500'>
                      This student has no active charges available to pause.
                    </p>
                  </CardContent>
                </Card>
              )}

              {pauseType === 'selected' && (
                <div className='mt-6'>
                  <h2 className='text-lg font-semibold leading-none tracking-tight'>
                    Active Classes
                  </h2>
                  <p className='text-sm text-muted-foreground'>
                    Choose which classes to pause charges for. Already paused
                    classes are listed under Resume Charges.
                  </p>
                  <ScrollArea className='mt-2 h-80 rounded-md border p-2'>
                    {hasActiveTuition ? (
                      activeTuition?.map((cls) => (
                        <div
                          key={cls.enrollmentId}
                          className='my-2 flex items-center justify-between rounded-lg border p-3'
                        >
                          <div className='flex items-center space-x-3'>
                            <Checkbox
                              id={cls.enrollmentId}
                              checked={selectedClasses.includes(
                                cls.enrollmentId,
                              )}
                              onCheckedChange={() => {
                                handleClassToggle(cls.enrollmentId);
                              }}
                            />
                            <div>
                              <Label
                                htmlFor={cls.enrollmentId}
                                className='cursor-pointer font-medium'
                              >
                                {cls.enrollmentName}
                              </Label>
                            </div>
                          </div>
                          <div className='flex items-center gap-1 font-semibold text-green-600'>
                            {currencyDetails?.symbol ?? '$'}
                            {cls.baseAmount.toFixed(2)}
                            <span className='ml-1 text-xs text-gray-500'>
                              /{cls.billingCycle.toLowerCase()}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className='flex h-full items-center justify-center text-center text-sm text-muted-foreground'>
                        This student has no active charges available to pause.
                      </div>
                    )}
                  </ScrollArea>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value='resume' className='mt-6 space-y-6'>
            <div className='space-y-4'>
              {pausedTuition && pausedTuition.length > 0 ? (
                <div className='mt-6'>
                  <h2 className='text-lg font-semibold leading-none tracking-tight'>
                    Paused Classes
                  </h2>
                  <p className='text-sm text-muted-foreground'>
                    Select which classes to resume charges for
                  </p>
                  <ScrollArea className='mt-2 h-80 rounded-md border p-2'>
                    {pausedTuition.map((cls) => (
                      <div
                        key={cls.enrollmentId}
                        className='my-2 flex items-center justify-between rounded-lg border p-3'
                      >
                        <div className='flex items-center space-x-3'>
                          <Checkbox
                            id={cls.enrollmentId}
                            checked={selectedClasses.includes(cls.enrollmentId)}
                            onCheckedChange={() => {
                              handleClassToggle(cls.enrollmentId);
                            }}
                          />
                          <div>
                            <Label
                              htmlFor={cls.enrollmentId}
                              className='cursor-pointer font-medium'
                            >
                              {cls.enrollmentName}
                            </Label>
                          </div>
                        </div>
                        <div className='flex items-center gap-1 font-semibold text-green-600'>
                          {currencyDetails?.symbol ?? '$'}
                          {cls.baseAmount.toFixed(2)}
                          <span className='ml-1 text-xs text-gray-500'>
                            /{cls.billingCycle.toLowerCase()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </ScrollArea>
                </div>
              ) : (
                <Card>
                  <CardContent className='flex flex-col items-center justify-center py-8 text-center'>
                    <h3 className='mb-2 text-lg font-medium text-gray-900'>
                      No Paused Charges
                    </h3>
                    <p className='text-gray-500'>
                      This student currently has no paused charges to resume.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant={'outline'}>Cancel</Button>
          </DialogClose>
          {selectedTab === 'pause' ? (
            <Button onClick={handleUpdateCharge} disabled={isSubmitDisabled}>
              {mutation.isPending && (
                <Loader2Icon className='mr-2 animate-spin' />
              )}
              Pause Charge
            </Button>
          ) : (
            <Button onClick={handleUpdateCharge} disabled={isSubmitDisabled}>
              {mutation.isPending && (
                <Loader2Icon className='mr-2 animate-spin' />
              )}
              Resume Charge
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
